import React, { useState } from "react";
import { Info } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { cn } from "../lib/utils";

interface FeatureTooltipProps {
  text: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  className?: string;
  isDynamic?: boolean;
  dynamicText?: (count: number) => string;
  selectedShowCount?: number;
}

export function FeatureTooltip({
  text,
  title,
  description,
  imageUrl,
  className,
  isDynamic,
  dynamicText,
  selectedShowCount = 0
}: FeatureTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasDetails = title || description || imageUrl;
  
  // Use dynamic text if available and show count is provided
  const displayText = isDynamic && dynamicText && selectedShowCount > 0 
    ? dynamicText(selectedShowCount) 
    : text;

  if (!hasDetails) {
    // If no details, just return the text without tooltip
    return <span className={className}>{displayText}</span>;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <span 
          className={cn(
            "cursor-pointer inline-flex items-center gap-1 hover:text-blue-600 transition-colors duration-200 group",
            className
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          {displayText}
          <Info className="w-3 h-3 opacity-40 group-hover:opacity-100 group-hover:text-blue-600 transition-all duration-200" />
        </span>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80" 
        side="right" 
        align="start"
        sideOffset={10}
      >
        <div className="space-y-3">
          {title && (
            <h4 className="font-semibold text-sm">{title}</h4>
          )}
          {description && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {description}
            </p>
          )}
          {imageUrl && (
            <div className="rounded-lg overflow-hidden border bg-gray-50">
              <img 
                src={imageUrl} 
                alt={title || text}
                className="w-full h-auto"
                onError={(e) => {
                  // Hide image if it fails to load
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}